<script setup>
import { computed } from 'vue';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { useI18n } from 'vue-i18n';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactId: { type: [Number, String], required: true },
});

const store = useStore();
const { locale, t } = useI18n();
const snapshots = computed(() =>
  store.getters['erpContacts/getSnapshots'](props.contactId)
);
const selectedExternalId = computed(() =>
  store.getters['erpContacts/getSelectedExternalId'](props.contactId)
);
const selectedIndex = computed(() =>
  snapshots.value.findIndex(
    item => item.external_client_id === selectedExternalId.value
  )
);
const snapshot = computed(() => snapshots.value[selectedIndex.value] || null);
const hasMultipleServices = computed(() => snapshots.value.length > 1);
const uiFlags = computed(() =>
  store.getters['erpContacts/getUIFlags'](props.contactId)
);
const unavailable = computed(() => t('CONVERSATION_SIDEBAR.ERP.UNAVAILABLE'));
const relativeTimeLocale = computed(() =>
  locale.value.startsWith('es') ? es : enUS
);
const display = value => value ?? unavailable.value;
const parseCoordinates = value => {
  if (typeof value !== 'string') return null;

  const parts = value.split(',').map(part => part.trim());
  if (parts.length !== 2 || parts.some(part => part === '')) return null;

  const [latitude, longitude] = parts.map(Number);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }

  return { latitude, longitude };
};
const coordinatesMapUrl = computed(() => {
  const parsed = parseCoordinates(snapshot.value?.coordinates);
  return parsed
    ? `https://maps.google.com/?q=${parsed.latitude},${parsed.longitude}`
    : null;
});
const fieldLabels = computed(() => ({
  serviceStatus: t('CONVERSATION_SIDEBAR.ERP.FIELDS.SERVICE_STATUS'),
  invoiceStatus: t('CONVERSATION_SIDEBAR.ERP.FIELDS.INVOICE_STATUS'),
  pendingBalance: t('CONVERSATION_SIDEBAR.ERP.FIELDS.PENDING_BALANCE'),
  username: t('CONVERSATION_SIDEBAR.ERP.FIELDS.USERNAME'),
  identityNumber: t('CONVERSATION_SIDEBAR.ERP.FIELDS.IDENTITY_NUMBER'),
  ipAddress: t('CONVERSATION_SIDEBAR.ERP.FIELDS.IP_ADDRESS'),
  coordinates: t('CONVERSATION_SIDEBAR.ERP.FIELDS.COORDINATES'),
  internetPlan: t('CONVERSATION_SIDEBAR.ERP.FIELDS.INTERNET_PLAN'),
  zone: t('CONVERSATION_SIDEBAR.ERP.FIELDS.ZONE'),
  cutoffDate: t('CONVERSATION_SIDEBAR.ERP.FIELDS.CUTOFF_DATE'),
  lastSync: t('CONVERSATION_SIDEBAR.ERP.FIELDS.LAST_SYNC'),
}));
const fields = computed(() => {
  if (!snapshot.value?.synced_at) return [];

  return [
    [
      'service_status',
      fieldLabels.value.serviceStatus,
      display(snapshot.value.service_status),
    ],
    [
      'invoice_status',
      fieldLabels.value.invoiceStatus,
      display(snapshot.value.invoice_status),
    ],
    [
      'pending_balance',
      fieldLabels.value.pendingBalance,
      snapshot.value.pending_balance == null
        ? unavailable.value
        : Number(snapshot.value.pending_balance).toFixed(2),
    ],
    ['username', fieldLabels.value.username, display(snapshot.value.username)],
    [
      'identity_number',
      fieldLabels.value.identityNumber,
      display(snapshot.value.identity_number),
    ],
    [
      'ip_address',
      fieldLabels.value.ipAddress,
      display(snapshot.value.ip_address),
    ],
    [
      'coordinates',
      fieldLabels.value.coordinates,
      display(snapshot.value.coordinates),
    ],
    [
      'internet_plan',
      fieldLabels.value.internetPlan,
      display(snapshot.value.internet_plan),
    ],
    ['zone', fieldLabels.value.zone, display(snapshot.value.zone)],
    [
      'cutoff_on',
      fieldLabels.value.cutoffDate,
      snapshot.value.cutoff_on
        ? format(parseISO(snapshot.value.cutoff_on), 'dd/MM/yyyy')
        : unavailable.value,
    ],
    [
      'last_sync',
      fieldLabels.value.lastSync,
      formatDistanceToNow(new Date(snapshot.value.synced_at), {
        addSuffix: true,
        locale: relativeTimeLocale.value,
      }),
    ],
  ];
});
const openCoordinates = () => {
  if (coordinatesMapUrl.value) {
    window.open(coordinatesMapUrl.value, '_blank', 'noopener,noreferrer');
  }
};
const failureMessage = computed(() => {
  if (snapshot.value?.sync_status !== 'failed') return null;

  return snapshot.value.synced_at
    ? t('CONVERSATION_SIDEBAR.ERP.FAILED_STALE')
    : t('CONVERSATION_SIDEBAR.ERP.FAILED_EMPTY');
});

const selectPrevious = () => {
  if (selectedIndex.value > 0) {
    store.commit('erpContacts/SET_ERP_SELECTED_SERVICE', {
      contactId: props.contactId,
      externalClientId:
        snapshots.value[selectedIndex.value - 1].external_client_id,
    });
  }
};
const selectNext = () => {
  if (selectedIndex.value < snapshots.value.length - 1) {
    store.commit('erpContacts/SET_ERP_SELECTED_SERVICE', {
      contactId: props.contactId,
      externalClientId:
        snapshots.value[selectedIndex.value + 1].external_client_id,
    });
  }
};

const sync = async () => {
  const result = await store.dispatch('erpContacts/sync', {
    conversationId: props.conversationId,
    contactId: props.contactId,
    force: true,
    manual: true,
  });
  if (!result.requestId) {
    useAlert(t('CONVERSATION_SIDEBAR.ERP.MANUAL_FAILED'));
    return;
  }
  if (result.manualResult) {
    const alertMessage = {
      success: t('CONVERSATION_SIDEBAR.ERP.MANUAL_SUCCESS'),
      partial: t('CONVERSATION_SIDEBAR.ERP.MANUAL_PARTIAL'),
      failed: t('CONVERSATION_SIDEBAR.ERP.MANUAL_FAILED'),
    }[result.manualResult];
    useAlert(alertMessage);
  }
};
</script>

<template>
  <div class="flex flex-col gap-3 p-3">
    <div class="flex flex-col gap-3">
      <div
        v-if="snapshot"
        class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
      >
        <span>
          {{
            hasMultipleServices
              ? $t('CONVERSATION_SIDEBAR.ERP.SERVICE_POSITION', {
                  id: snapshot.external_client_id,
                  position: selectedIndex + 1,
                  total: snapshots.length,
                })
              : $t('CONVERSATION_SIDEBAR.ERP.SERVICE', {
                  id: snapshot.external_client_id,
                })
          }}
        </span>
        <div v-if="hasMultipleServices" class="flex items-center gap-1">
          <NextButton
            data-testid="erp-previous-service"
            sm
            faded
            slate
            icon="i-lucide-chevron-left"
            :disabled="selectedIndex === 0"
            :aria-label="$t('CONVERSATION_SIDEBAR.ERP.PREVIOUS_SERVICE')"
            @click="selectPrevious"
          />
          <NextButton
            data-testid="erp-next-service"
            sm
            faded
            slate
            icon="i-lucide-chevron-right"
            :disabled="selectedIndex === snapshots.length - 1"
            :aria-label="$t('CONVERSATION_SIDEBAR.ERP.NEXT_SERVICE')"
            @click="selectNext"
          />
        </div>
      </div>
      <p v-if="failureMessage" class="text-xs text-n-ruby-11">
        {{ failureMessage }}
      </p>
      <Spinner
        v-if="(uiFlags.isFetching || uiFlags.isSyncing) && !fields.length"
        :size="16"
      />
      <div v-if="fields.length" class="flex flex-col gap-3">
        <div
          v-for="[key, label, value] in fields"
          :key="key"
          class="flex flex-col gap-1"
        >
          <span
            data-testid="erp-field-label"
            class="text-xs font-medium text-n-slate-11"
          >
            {{ label }}
          </span>
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm break-words text-n-slate-12">
              {{ value }}
            </span>
            <NextButton
              v-if="key === 'coordinates' && coordinatesMapUrl"
              v-tooltip.left="
                $t('CONVERSATION_SIDEBAR.ERP.ACTIONS.OPEN_IN_GOOGLE_MAPS')
              "
              data-testid="erp-open-coordinates"
              sm
              ghost
              slate
              icon="i-lucide-map-pin"
              :aria-label="
                $t('CONVERSATION_SIDEBAR.ERP.ACTIONS.OPEN_IN_GOOGLE_MAPS')
              "
              @click="openCoordinates"
            />
          </div>
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION_SIDEBAR.ERP.EMPTY') }}
      </p>
    </div>
    <NextButton
      data-testid="erp-sync-button"
      sm
      faded
      slate
      icon="i-lucide-refresh-ccw"
      :is-loading="uiFlags.isSyncing"
      :disabled="uiFlags.isSyncing"
      :label="$t('CONVERSATION_SIDEBAR.ERP.SYNC')"
      @click="sync"
    />
  </div>
</template>
